import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Alert,
  Chip,
  Button
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';

/**
 * 故事结构生成进度面板（高科技指挥中心风格）
 * 各阶段预计时间根据章节数动态计算，基于实测数据推导
 *
 * 实测数据参考（600章，145.3分钟）：
 *   架构分析（后端 stage 1）：~7分钟（阶段划分+剧情线+角色画像+关键情节点+结局控制+一致性上下文）
 *   结构规划（后端 stage 2-3）：~3分钟（架构规划+模块细化）
 *   章节生成（后端 stage 4）：~126分钟（批次生成，每章约12.6秒）
 *   数据校验（后端 stage 5-6）：~3分钟（一致性规则+AI规划+数据注入）
 *
 * 前端 phase 与后端 stage 的对应关系：
 *   phase 0 架构分析：后端 stage 1（核心框架）
 *   phase 1 结构规划：后端 stage 2-3（架构规划/模块细化）
 *   phase 2 章节生成：后端 stage 4（章节蓝图批次生成）
 *   phase 3 数据校验：后端 stage 5-6（一致性校验/数据整合/AI规划）
 */

// 根据总章节数计算各阶段预估时间（分钟），向上取整
const calcPhaseMinutes = (totalChapters) => {
  const n = totalChapters || 30;
  // 章节生成 = 批次生成，固定每10章约2分钟（实测：600章126分钟）
  const chapterGenMinutes = Math.ceil(n / 10 * 2);
  return {
    phase0: 15,                                              // 架构分析（stage 1，约7分钟，加缓冲）
    phase1: 5,                                               // 结构规划（stage 2-3，约3分钟，加缓冲）
    phase2: chapterGenMinutes,                                // 章节生成（stage 4，批次生成）
    phase3: 5,                                               // 数据校验（stage 5-6，约3分钟，加缓冲）
  };
};

// 将分钟转为秒的便捷函数
const minToSec = (m) => m * 60;

const PHASE_ESTIMATES = (totalChapters) => [
  { name: '架构分析', seconds: minToSec(calcPhaseMinutes(totalChapters).phase0) },
  { name: '结构规划', seconds: minToSec(calcPhaseMinutes(totalChapters).phase1) },
  { name: '章节生成', seconds: minToSec(calcPhaseMinutes(totalChapters).phase2) },
  { name: '数据校验', seconds: minToSec(calcPhaseMinutes(totalChapters).phase3) },
];

// localStorage key for storing generation start time
const getStorageKey = (novelId) => `story_gen_start_${novelId || 'default'}`;

// 尝试从 localStorage 恢复开始时间（兼容后端 elapsedTime）
const getStartTime = (novelId, backendElapsed) => {
  const key = getStorageKey(novelId);
  const saved = localStorage.getItem(key);
  if (saved) {
    const startTime = parseInt(saved, 10);
    if (backendElapsed && backendElapsed > 0) {
      return { startTime, elapsedTime: backendElapsed, source: 'backend' };
    }
    return { startTime, elapsedTime: Math.floor((Date.now() - startTime) / 1000), source: 'local' };
  }
  if (backendElapsed && backendElapsed > 0) {
    const newStartTime = Date.now() - backendElapsed * 1000;
    localStorage.setItem(key, String(newStartTime));
    return { startTime: newStartTime, elapsedTime: backendElapsed, source: 'backend' };
  }
  return { startTime: null, elapsedTime: 0, source: null };
};

// 每个阶段的专属配色主题
const PHASE_COLORS = [
  { primary: '#ff6b35', secondary: '#ffd700', label: '橙金', glow: 'rgba(255,107,53,0.5)' },
  { primary: '#00d9ff', secondary: '#ffd700', label: '电蓝', glow: 'rgba(0,217,255,0.5)' },
  { primary: '#a855f7', secondary: '#00ff88', label: '紫绿', glow: 'rgba(168,85,247,0.5)' },
  { primary: '#00ff88', secondary: '#00d9ff', label: '绿蓝', glow: 'rgba(0,255,136,0.5)' },
];

// 阶段标签（与 UI 指示器一一对应，共4个阶段）
const PHASE_LABELS = [
  { name: '架构分析', desc: '语义解析·角色画像', icon: '◈' },
  { name: '结构规划', desc: '剧情线·章节大纲', icon: '◇' },
  { name: '章节生成', desc: '批量生成·内容构建', icon: '◆' },
  { name: '数据校验', desc: '一致性检测·优化', icon: '◉' },
];

/**
 * 根据后端 stage 和前端 progress 计算当前 UI 阶段索引（phase 0-3）
 * 核心原则：phase 只升不降，防止后端流式批次更新导致指示器跳回
 *
 * 后端 stage 含义：
 *   0  = 初始化
 *   1  = 核心框架
 *   2  = 架构规划
 *   3  = 模块细化
 *   4  = 章节蓝图（批次生成）
 *   5  = 一致性校验 / 数据整合
 *   6  = AI 智能规划
 *
 * phase 阶段定义：
 *   0 = 架构分析（后端 stage 1，核心框架开始）
 *   1 = 结构规划（后端 stage 2-3，架构规划/模块细化）
 *   2 = 章节生成（后端 stage 4，章节蓝图批次生成）
 *   3 = 数据校验（后端 stage 5-6，一致性校验/数据整合/AI规划）
 */
const computeCurrentPhase = (progress) => {
  if (progress.completed) return 3;

  const backendStage = progress.stage || 0;

  if (backendStage === 1) {
    return 0; // 核心框架 → 架构分析
  }

  if (backendStage === 2 || backendStage === 3) {
    return 1; // 架构规划/模块细化 → 结构规划
  }

  if (backendStage === 4) {
    return 2; // 章节蓝图 → 章节生成
  }

  if (backendStage >= 5) {
    return 3; // 一致性校验/数据整合/AI规划 → 数据校验
  }

  return 0; // 初始化 → 架构分析
};

const StoryStructureProgressPanel = ({ novelId, onComplete, onClose, totalChapters = 30, initialProgress = null, onRegenerate }) => {
  // 初始状态：从 initialProgress 和 localStorage 恢复开始时间（lazy init，保证刷新后不归零）
  const getInitialState = () => {
    const backendElapsed = initialProgress?.elapsedTime || 0;
    const { startTime, elapsedTime } = getStartTime(novelId, backendElapsed);
    return { startTime, elapsedTime };
  };

  const [startTime, setStartTime] = useState(getInitialState().startTime);
  const [elapsedTime, setElapsedTime] = useState(getInitialState().elapsedTime);

  const [progress, setProgress] = useState(initialProgress || {
    stage: 0,
    stageName: '初始化',
    stageDescription: 'KM-AI 智能结构引擎正在处理数据...',
    progress: 0,
    completed: false,
    batch: null,
    totalBatches: null,
    completedChapters: null,
    totalChapters: null,
    elapsedTime: 0,
  });

  const [error, setError] = useState(null);
  const [failed, setFailed] = useState(false);

  // 各阶段预估时间（根据章节数动态计算）
  const phaseTimes = PHASE_ESTIMATES(progress.totalChapters || totalChapters || 30);

  // 当前 UI 阶段索引（phase 0-3），始终不会跳回
  const currentPhase = computeCurrentPhase(progress);

  // 已完成阶段累计秒数（基于前端 phase 索引）
  const completedSeconds = phaseTimes.slice(0, Math.max(0, currentPhase)).reduce((s, p) => s + p.seconds, 0);
  const totalSeconds = phaseTimes.reduce((s, p) => s + p.seconds, 0);
  // 当前阶段预估时间（按 UI phase 索引查表）
  const currentPhaseData = phaseTimes[currentPhase];

  // 生成开始时记录开始时间到 localStorage
  useEffect(() => {
    if (progress.stage > 0 && !startTime && !progress.completed) {
      const now = Date.now();
      setStartTime(now);
      localStorage.setItem(getStorageKey(novelId), String(now));
    }
  }, [progress.stage, progress.completed, startTime, novelId]);

  // 生成完成时清除 localStorage
  useEffect(() => {
    if (progress.completed) {
      localStorage.removeItem(getStorageKey(novelId));
    }
  }, [progress.completed, novelId]);

  // 前端每秒 ticker 更新已用时间（同时跟踪后端 elapsedTime 取最大值）
  useEffect(() => {
    if (progress.completed || progress.stage === 0) return;
    const tick = () => {
      setElapsedTime(prev => {
        const fromStart = startTime ? Math.floor((Date.now() - startTime) / 1000) : prev;
        const fromBackend = progress.elapsedTime || 0;
        return Math.max(fromStart, fromBackend);
      });
    };
    tick(); // 立即更新一次
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [progress.completed, progress.stage, startTime, progress.elapsedTime]);

  // 同步后端进度数据
  useEffect(() => {
    if (!initialProgress) return;
    setProgress(prev => ({
      ...prev,
      stage: initialProgress.stage ?? prev.stage,
      stageName: initialProgress.stageName ?? prev.stageName,
      stageDescription: (initialProgress.stageDescription ?? initialProgress.message) ?? prev.stageDescription,
      progress: initialProgress.progress ?? prev.progress,
      completed: initialProgress.completed ?? prev.completed,
      batch: initialProgress.batch ?? prev.batch,
      totalBatches: initialProgress.totalBatches ?? prev.totalBatches,
      completedChapters: initialProgress.completedChapters ?? prev.completedChapters,
      totalChapters: initialProgress.totalChapters ?? prev.totalChapters,
      retrying: initialProgress.retrying ?? prev.retrying,
      elapsedTime: initialProgress.elapsedTime ?? prev.elapsedTime,
    }));
  }, [initialProgress]);

  const formatTime = (seconds) => {
    if (seconds < 60) return `${seconds}秒`;
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return s > 0 ? `${m}分${s}秒` : `${m}分钟`;
  };

  const formatMin = (minutes) => {
    if (minutes < 60) return `${minutes}分钟`;
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return m > 0 ? `${h}小时${m}分钟` : `${h}小时`;
  };

  return (
    <Box className="ssp-root">
      {/* === 背景层 === */}
      <Box className="ssp-grid-bg" />
      <Box className="ssp-scanline" />
      <Box className="ssp-particles">
        {[...Array(12)].map((_, i) => (
          <Box key={i} className={`ssp-particle ssp-particle-${i + 1}`} />
        ))}
      </Box>

      {/* === 英雄头部 === */}
      <Box className="ssp-hero">
        <Box className="ssp-hero-content">

          {/* ARC PROGRESS: fused timer + percent */}
          {!progress.completed && (
            <Box className="ssp-arc-container">
              <svg className="ssp-arc-svg" viewBox="0 0 200 200">
                {[...Array(12)].map((_, i) => {
                  const angle = i * 30 - 90;
                  const rad = (angle * Math.PI) / 180;
                  const x1 = 100 + 90 * Math.cos(rad);
                  const y1 = 100 + 90 * Math.sin(rad);
                  const x2 = 100 + 82 * Math.cos(rad);
                  const y2 = 100 + 82 * Math.sin(rad);
                  return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} className="ssp-arc-tick" />;
                })}
                <circle cx="100" cy="100" r="76" className="ssp-arc-inner-track" />
                <circle cx="100" cy="100" r="83" className="ssp-arc-progress-track" />
                {/* stroke-dasharray 方案：圆周长 = 2π×83，无任何坐标计算 */}
                {/* rotate(-90deg) 让起点从顶部开始，dashoffset 控制进度 */}
                <circle
                  cx="100" cy="100" r="83"
                  className="ssp-arc-progress-fill"
                  style={{
                    transform: 'rotate(-90deg)',
                    transformOrigin: '100px 100px',
                    strokeDasharray: 2 * Math.PI * 83,
                    strokeDashoffset: 2 * Math.PI * 83 * (1 - Math.min(progress.progress, 100) / 100),
                    transition: 'stroke-dashoffset 0.5s ease',
                  }}
                />
                {/* 圆点：沿圆轨道旋转，transformOrigin 为圆心 */}
                <circle
                  cx="100" cy="17" r="5" className="ssp-arc-head"
                  style={{
                    transform: `rotate(${progress.progress * 3.6}deg)`,
                    transformOrigin: '100px 100px',
                  }}
                />
              </svg>
              <Box className="ssp-arc-center">
                <Box className="ssp-arc-percent-row">
                  <Typography className="ssp-arc-percent">{progress.progress}</Typography>
                  <Typography className="ssp-arc-percent-sym">%</Typography>
                </Box>
                <Box className="ssp-arc-divider" />
                <Box className="ssp-arc-elapsed">
                  <Typography className="ssp-arc-elapsed-time">{formatTime(elapsedTime)}</Typography>
                  <Typography className="ssp-arc-elapsed-label">已耗时</Typography>
                </Box>
                <Box className="ssp-arc-status-dot" />
              </Box>
            </Box>
          )}
          {progress.completed && (
            <Box className="ssp-complete-icon">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                <circle cx="24" cy="24" r="20" stroke="#00ff88" strokeWidth="3" />
                <path d="M14 24L21 31L34 17" stroke="#00ff88" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </Box>
          )}

          {/* 主标题 */}
          <Box className="ssp-hero-title-container">
            <Typography
              className="ssp-hero-title"
              data-text={progress.completed ? '生成完成' : '故事结构生成中'}
            >
              {progress.completed ? '生成完成' : '故事结构生成中'}
            </Typography>
          </Box>

          {/* 当前阶段预计耗时 */}
          {!progress.completed && (
            <Box className="ssp-cur-phase">
              <Box className="ssp-cur-phase-label">当前阶段</Box>
              <Box className="ssp-cur-phase-info">
                <Box className="ssp-cur-phase-name">{currentPhaseData?.name || PHASE_LABELS[currentPhase]?.name || ''}</Box>
                <Box className="ssp-cur-phase-sep">/</Box>
                <Box className="ssp-cur-phase-est">{currentPhaseData ? `约${formatMin(Math.ceil(currentPhaseData.seconds / 60))}` : '—'}</Box>
              </Box>
            </Box>
          )}

          {/* 阶段流程指示器 */}
          {!progress.completed && (
            <Box className="ssp-phase-flow">
              {PHASE_LABELS.map((phase, idx) => {
                const isDone = currentPhase > idx;
                const isActive = currentPhase === idx;
                const colors = PHASE_COLORS[idx];
                return (
                  <React.Fragment key={idx}>
                    <Box className={`ssp-phase-node ${isDone ? 'done' : ''} ${isActive ? 'active' : ''}`}>
                      <Box className="ssp-phase-node-dot"
                        style={isActive ? { borderColor: colors.primary, boxShadow: `0 0 8px ${colors.glow}` } : {}}
                      />
                      <Box className="ssp-phase-node-label"
                        style={isActive ? { color: colors.primary } : isDone ? { color: 'rgba(255,255,255,0.4)' } : {}}
                      >
                        {phase.name}
                      </Box>
                    </Box>
                    {idx < PHASE_LABELS.length - 1 && (
                      <Box className={`ssp-phase-connector ${isDone ? 'done' : ''}`} />
                    )}
                  </React.Fragment>
                );
              })}
            </Box>
          )}

          {/* 当前状态 */}
          {!progress.completed && (
            <Box className="ssp-state-bar">
              <Box className="ssp-state-bar-frame">
                <Box className="ssp-state-bar-glint" />
                <Box className="ssp-state-bar-corner-bl" />
                <Box className="ssp-state-bar-corner-br" />

                <Box className="ssp-state-bar-icon">
                  <Box className="ssp-state-bar-radar" />
                </Box>

                <Box className="ssp-state-bar-divider" />

                <Box className="ssp-state-bar-text-wrap">
                  <Typography className="ssp-state-bar-text">
                    {progress.stageDescription}
                  </Typography>
                </Box>

                <Box className="ssp-state-bar-right">
                  <Box className="ssp-state-bar-badge">
                    <Box className="ssp-state-bar-badge-dot" />
                    <Typography className="ssp-state-bar-badge-text">LIVE</Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          )}

          {/* 批次进度（章节蓝图阶段时显示） */}
          {!progress.completed && progress.stage === 4 && progress.totalBatches && (
            <Box className="ssp-batch-row">
              <Chip label={`批次 ${progress.batch}/${progress.totalBatches}`} className="ssp-chip ssp-chip-blue" size="small" />
              <Chip label={`章节 ${progress.completedChapters}/${progress.totalChapters}`} className="ssp-chip ssp-chip-green" size="small" />
            </Box>
          )}
        </Box>
      </Box>

      {/* === 系统面板 === */}
      {!progress.completed && (
        <Box className="ssp-system-panel">

          {/* 第一行：三枚战术卡片 */}
          <Box className="ssp-command-cards">

            {/* 卡片1：生成规模 */}
            <Box className="ssp-cmd-card ssp-cmd-card--cyan">
              <Box className="ssp-cmd-card-glow" />
              <Box className="ssp-cmd-card-inner">
                <Box className="ssp-cmd-icon-wrap">
                  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                    <rect x="3" y="5" width="26" height="22" rx="3" stroke="#00d9ff" strokeWidth="1.5" />
                    <line x1="8" y1="11" x2="24" y2="11" stroke="#00d9ff" strokeWidth="1.5" strokeLinecap="round" />
                    <line x1="8" y1="16" x2="18" y2="16" stroke="#00d9ff" strokeWidth="1.5" strokeLinecap="round" />
                    <line x1="8" y1="21" x2="21" y2="21" stroke="#00d9ff" strokeWidth="1.5" strokeLinecap="round" />
                    <circle cx="24" cy="21" r="4" fill="rgba(0,217,255,0.2)" stroke="#00d9ff" strokeWidth="1" />
                    <line x1="22" y1="21" x2="26" y2="21" stroke="#00d9ff" strokeWidth="1" strokeLinecap="round" />
                  </svg>
                </Box>
                <Box className="ssp-cmd-text">
                  <Typography className="ssp-cmd-label">生成规模</Typography>
                  <Typography className="ssp-cmd-value">共&nbsp;<span className="ssp-cmd-highlight">{totalChapters}</span>&nbsp;章</Typography>
                </Box>
                <Box className="ssp-cmd-badge">MASSIVE</Box>
              </Box>
            </Box>

            {/* 卡片2：智能后台处理 */}
            <Box className="ssp-cmd-card ssp-cmd-card--green">
              <Box className="ssp-cmd-card-glow" />
              <Box className="ssp-cmd-card-inner">
                <Box className="ssp-cmd-icon-wrap">
                  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                    <circle cx="16" cy="16" r="5" fill="#00ff88" />
                    <circle cx="16" cy="16" r="10" stroke="#00ff88" strokeWidth="1.5" strokeDasharray="4 3" />
                    <circle cx="16" cy="16" r="14" stroke="rgba(0,255,136,0.3)" strokeWidth="1" strokeDasharray="2 4" />
                    <circle cx="16" cy="2" r="2" fill="#00ff88" />
                    <circle cx="16" cy="30" r="2" fill="#00ff88" />
                    <circle cx="2" cy="16" r="2" fill="#00ff88" />
                    <circle cx="30" cy="16" r="2" fill="#00ff88" />
                  </svg>
                </Box>
                <Box className="ssp-cmd-text">
                  <Typography className="ssp-cmd-label">智能后台处理</Typography>
                  <Typography className="ssp-cmd-value ssp-cmd-value--small">可关闭页面，稍后回来查看生成结果</Typography>
                </Box>
                <Box className="ssp-cmd-badge ssp-cmd-badge--green">BACKGROUND</Box>
              </Box>
            </Box>

            {/* 卡片3：AI深度分析 */}
            <Box className="ssp-cmd-card ssp-cmd-card--gold">
              <Box className="ssp-cmd-card-glow" />
              <Box className="ssp-cmd-card-inner">
                <Box className="ssp-cmd-icon-wrap">
                  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                    <path d="M16 4L18.5 12H27L20 17.5L22.5 26L16 21L9.5 26L12 17.5L5 12H13.5L16 4Z" stroke="#ffd700" strokeWidth="1.5" strokeLinejoin="round" />
                    <circle cx="16" cy="16" r="4" fill="rgba(255,215,0,0.2)" stroke="#ffd700" strokeWidth="1" />
                  </svg>
                </Box>
                <Box className="ssp-cmd-text">
                  <Typography className="ssp-cmd-label">AI 深度分析</Typography>
                  <Typography className="ssp-cmd-value ssp-cmd-value--small">多模型协同推理，智能构建故事结构</Typography>
                </Box>
                <Box className="ssp-cmd-badge ssp-cmd-badge--gold">NEURAL</Box>
              </Box>
            </Box>
          </Box>

          {/* 第二行：战术提示条 */}
          <Box className="ssp-tactical-bar">
            <Box className="ssp-tactical-bar-inner">
              <Box className="ssp-tactical-icon">
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M9 2L10.5 7H16L11.5 10.5L13 16L9 12.5L5 16L6.5 10.5L2 7H7.5L9 2Z" fill="#ffd700" />
                </svg>
              </Box>
              <Box className="ssp-tactical-scroll-wrap">
                <Typography className="ssp-tactical-scroll-text">
                  系统将依次完成：架构分析 → 结构规划 → 章节生成 → 数据校验
                  &nbsp;&nbsp;&nbsp;•&nbsp;&nbsp;&nbsp;
                  可关闭页面，稍后返回查看结果
                  &nbsp;&nbsp;&nbsp;•&nbsp;&nbsp;&nbsp;
                  生成规模：{totalChapters} 章，预计耗时 {formatMin(Math.ceil(totalSeconds / 60))}
                </Typography>
              </Box>
            </Box>
          </Box>

        </Box>
      )}

      {/* === 重试警告 === */}
      {progress.retrying && (
        <Alert
          severity="warning"
          className="ssp-alert"
        >
          正在重新连接服务器，请稍候...
        </Alert>
      )}

      {/* === 错误提示 === */}
      {error && (
        <Box className="ssp-error-panel">
          <Typography className="ssp-error-title">{error}</Typography>
        </Box>
      )}

      {/* === 完成提示 === */}
      {progress.completed && (
        <Box className="ssp-complete-panel">
          <Box className="ssp-complete-glow" />
          <Typography className="ssp-complete-title">故事结构生成完成</Typography>
          <Typography className="ssp-complete-subtitle">您可以在下方查看详细内容</Typography>
        </Box>
      )}

      {/* === 失败面板 === */}
      {failed && (
        <Box className="ssp-failed-panel">
          <Typography className="ssp-failed-title">故事结构生成失败</Typography>
          <Typography className="ssp-failed-subtitle">生成过程中发生错误</Typography>

          <Box className="ssp-failed-reasons">
            <Typography className="ssp-failed-reasons-title">可能的原因：</Typography>
            <Typography className="ssp-failed-reason-item">AI 服务暂时不可用</Typography>
            <Typography className="ssp-failed-reason-item">网络连接不稳定</Typography>
            <Typography className="ssp-failed-reason-item">大纲内容格式有问题</Typography>
          </Box>

          <Box className="ssp-failed-regen-info">
            <Typography className="ssp-failed-regen-title">重新生成说明：</Typography>
            <Typography className="ssp-failed-regen-item">清除所有已生成的故事结构数据</Typography>
            <Typography className="ssp-failed-regen-item">从头开始重新生成完整的故事结构</Typography>
            <Typography className="ssp-failed-regen-item">确保故事结构的完整性和一致性</Typography>
          </Box>

          {onRegenerate && (
            <Button
              variant="contained"
              size="large"
              startIcon={<RefreshIcon />}
              onClick={() => {
                setFailed(false);
                if (onRegenerate) onRegenerate();
              }}
              className="ssp-regen-btn"
            >
              重新生成故事结构
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};

export default StoryStructureProgressPanel;
